import _ from 'lodash';
import { $each, $end, $none, $set, update } from 'qim';

import { createReducer } from '@zapier/toolbox-redux-framework';

import ActionTypes from 'app/developer-v3/legacy/ActionTypes';

import { EmptyAppStatsShape } from './constants';

const initialState = {
  appNotificationSettings: undefined,
  persistedCCContactEmail: undefined,
  currentAppId: undefined,
  currentIssueId: undefined,
  cloneAppId: undefined,
  eventStats: undefined,
  eventList: undefined,
  deployStatus: {},
  deployHistory: [],
  usageStats: undefined,
  appsBeingDeleted: {},
  loadingStates: {
    appIssueDetail: false,
    appIssues: false,
    deployStatus: false,
    deployHistory: false,
    cloning: false,
    eventStats: false,
    eventList: false,
    usageStats: false,
    cliUsage: false,
    cliStats: false,
    cliPromote: false,
  },
  sidebarSectionBeingEdited: undefined,
  commentIdBeingEdited: undefined,
  savingStates: {
    newComment: false,
    commentId: undefined,
    newTeamMember: false,
  },
  savedStates: {
    shouldNotifyDeveloper: undefined,
    ccContactEmail: undefined,
    teamMemberEmail: undefined,
    teamMemberRole: undefined,
    teamMemberWelcome: undefined,
  },
  saveErrors: {
    shouldNotifyDeveloper: undefined,
    ccContactEmail: undefined,
    newTeamMember: undefined,
  },
  didScrollErrors: false,
  newApp: {
    title: '',
    description: '',
    intention: '',
    role: '',
    app_category: '',
  },
  app: {
    id: 0,
    title: '',
    description: '',
    intention: '',
    role: '',
    app_category: '',
    version: '',
  },
  currentCliAppId: 0,
  currentCliAppVersion: '',
  cliUsage: undefined,
  cliStats: _.cloneDeep(EmptyAppStatsShape),
  savingApp: false,
  collaboratorEmail: '',
  inviteeEmail: '',
  activation: {
    url: '',
    marketing_contact_email: '',
    technical_contact_email: '',
    twitter_username: '',
    test_account_username: '',
    test_account_password: '',
    paid_api: 'no',
    primary_color: '',
  },
  fieldErrors: {},
  errors: [],
  messages: [],
  formFields: {},
  issues: undefined,
  issueDetails: {},
  issueTags: [],
  newIssueComments: {},
  taggableUsers: [],
  cliPromote: {
    activationUrl: undefined,
    errors: [],
  },
};

const toggleIssueTag = (state, appId, issueId, tagId, isAdding) => {
  let issueTags = _.map(
    _.get(state.issueDetails, [appId, issueId, 'tags'], [])
  );
  if (isAdding) {
    const tag = _.find(state.issueTags, { id: tagId });
    issueTags.push(tag);
  } else {
    // issueTags = _.remove(issueTags, (tag) => tag.id === tagId);
    issueTags = _.remove(
      _.map(issueTags, tag => {
        return tag.id !== tagId ? tag : null;
      })
    );
  }
  return update(
    ['issueDetails', appId, issueId, 'tags', $set(issueTags)],
    state
  );
};

const toggleIssueAction = (
  state,
  appId,
  issueId,
  category,
  actionKey,
  isAdding
) => {
  const lokiCategory = {
    triggers: 'trigger',
    actions: 'action',
    searches: 'search',
  }[category];

  const allActions = _.defaults(
    {},
    _.get(state, ['issueDetails', appId, issueId, 'actions'], {})
  );
  if (Object.keys(allActions).indexOf(lokiCategory) === -1) {
    allActions.lokiCategory = [];
  }

  allActions[lokiCategory] = _.map(allActions[lokiCategory], key => key);
  const indexOf = allActions[lokiCategory].indexOf(actionKey);
  if (isAdding) {
    if (indexOf === -1) {
      allActions[lokiCategory].push(actionKey);
    }
  } else {
    if (indexOf !== -1) {
      allActions[lokiCategory].splice(indexOf, indexOf + 1);
    }
  }

  return update(
    ['issueDetails', appId, issueId, 'actions', $set(allActions)],
    state
  );
};

const refreshIssue = (appId, issue, state) => {
  const oldIssue = _.get(state, ['issueDetails', [appId], [issue.id]], {});
  if (
    !oldIssue.updatedTimestampId ||
    issue.updatedTimestampId > oldIssue.updatedTimestampId
  ) {
    state = update(['issueDetails', appId, issue.id, $set(issue)], state);
  }
  return state;
};

const updateSingleKey = (key, value, state) =>
  update([key, $set(value)], state);

const updateLoadingState = (key, value, state) =>
  update(['loadingStates', key, $set(value)], state);

const updateFormErrors = (formErrors, state) =>
  update(
    [
      ['fieldErrors', $set(formErrors.fieldErrors)],
      ['errors', $set(formErrors.errors)],
    ],
    state
  );

const updateAppBeingDeleted = (appId, value, state) =>
  update(['appsBeingDeleted', appId, $set(value)], state);

const cleanErrorsOnUpdate = (fieldName, value, state) => {
  // Remove errors as the user types
  const fieldErrors =
    value && state.fieldErrors[fieldName]
      ? _.omit(state.fieldErrors, fieldName)
      : state.fieldErrors;

  const formErrors = {
    errors: [],
    fieldErrors,
  };

  return updateFormErrors(formErrors, state);
};

const updateAppNotificationSettings = (state, result) => {
  if (_.isObject(result) && Object.keys(result).length > 0) {
    state = update(['appNotificationSettings', $set(result)], state);
    const ccContactEmail = _.get(result, ['cc_contact', 'email']);
    return update(['persistedCCContactEmail', $set(ccContactEmail)], state);
  }
  return state;
};

const updateSaveErrorMessage = (key, value, state) => {
  return update(['saveErrors', key, $set(value)], state);
};

const updateSavedState = (key, value, state) => {
  return update(['savedStates', key, $set(value)], state);
};

const createMessage = ({ id = _.uniqueId('dev-msg-'), type, message }, state) =>
  update(['messages', $end, $set({ id, type, message })], state);

const handlers = {
  [ActionTypes.SET_SAVE_ERROR_MESSAGE](state, { key, value }) {
    return updateSaveErrorMessage(key, value, state);
  },

  [ActionTypes.SET_SAVED_STATE](state, { key, value }) {
    state = updateSavedState(key, value, state);
    state = updateSaveErrorMessage(key, undefined, state);
    return state;
  },

  [ActionTypes.LOAD_APP_NOTIFICATION_SETTINGS_DONE](state, { result }) {
    return updateAppNotificationSettings(state, result);
  },

  [ActionTypes.CHANGE_CC_CONTACT_EMAIL](state, { email, shouldClearError }) {
    const key = 'ccContactEmail';
    state = update(
      ['appNotificationSettings', 'cc_contact', 'email', $set(email)],
      state
    );
    if (shouldClearError) {
      state = updateSaveErrorMessage(key, undefined, state);
      state = updateSaveErrorMessage('shouldNotifyDeveloper', undefined, state);
    }
    state = updateSavedState(key, undefined, state);
    return state;
  },

  [ActionTypes.SAVE_CC_CONTACT_EMAIL](state) {
    const key = 'ccContactEmail';
    state = updateSavedState(key, undefined, state);
    state = updateSaveErrorMessage(key, undefined, state);
    return state;
  },

  [ActionTypes.SAVE_CC_CONTACT_EMAIL_DONE](state, { result }) {
    const key = 'ccContactEmail';
    state = updateAppNotificationSettings(state, result);
    state = updateSavedState(key, true, state);
    state = updateSaveErrorMessage(key, undefined, state);
    return state;
  },

  [ActionTypes.SAVE_CC_CONTACT_EMAIL_FAIL](state) {
    const key = 'ccContactEmail';
    return updateSavedState(key, false, state);
  },

  [ActionTypes.CHANGE_DEVELOPER_NOTIFICATIONS](state, { shouldNotify }) {
    const key = 'shouldNotifyDeveloper';
    state = updateSaveErrorMessage(key, undefined, state);
    return update(
      [
        'appNotificationSettings',
        'should_notify_developer',
        $set(shouldNotify),
      ],
      state
    );
  },

  [ActionTypes.CHANGE_DEVELOPER_NOTIFICATIONS_DONE](state, { result }) {
    const key = 'shouldNotifyDeveloper';
    state = updateAppNotificationSettings(state, result);
    state = updateSavedState(key, true, state);
    return state;
  },

  [ActionTypes.CHANGE_DEVELOPER_NOTIFICATIONS_FAIL](state, { shouldNotify }) {
    const key = 'shouldNotifyDeveloper';
    state = updateSavedState(key, false, state);
    return update(
      [
        'appNotificationSettings',
        'should_notify_developer',
        $set(!shouldNotify),
      ],
      state
    );
  },

  [ActionTypes.UPDATE_ASSIGNEE](state, { appId, issueId, newAssignee }) {
    const previousAssignee = _.get(state, [
      'issueDetails',
      appId,
      issueId,
      'assignee',
    ]);
    return update(
      [
        [
          'issueDetails',
          appId,
          issueId,
          ['assignee', $set(newAssignee)],
          ['previousAssignee', $set(previousAssignee)],
        ],
        ['sidebarSectionBeingEdited', $set(undefined)],
      ],
      state
    );
  },

  [ActionTypes.UPDATE_ASSIGNEE_DONE](state, { appId, issue, timestampId }) {
    issue = _.defaults({}, issue);
    issue.updatedTimestampId = timestampId;
    return refreshIssue(appId, issue, state);
  },

  [ActionTypes.UPDATE_ASSIGNEE_FAIL](state, { appId, issueId }) {
    const previousAssignee = _.get(state, [
      'issueDetails',
      appId,
      issueId,
      'previousAssignee',
    ]);
    return update(
      [
        'issueDetails',
        appId,
        issueId,
        ['assignee', $set(previousAssignee)],
        ['previousAssignee', $set(undefined)],
      ],
      state
    );
  },

  [ActionTypes.CHANGE_COMMENT](state, { value, commentId, appId, issueId }) {
    return !commentId
      ? update(['newIssueComments', appId, issueId, $set(value)], state)
      : update(
          [
            'issueDetails',
            appId,
            issueId,
            'comments',
            commentId,
            'body',
            $set(value.text),
          ],
          state
        );
  },

  [ActionTypes.SUBMIT_NEW_COMMENT](state) {
    return update(['savingStates', 'newComment', $set(true)], state);
  },

  [ActionTypes.SUBMIT_NEW_COMMENT_DONE](state, { appId, comment }) {
    return update(
      [
        ['savingStates', 'newComment', $set(false)],
        [
          'newIssueComments',
          appId,
          comment.issue.id,
          $set({
            text: '',
            selection: null,
          }),
        ],
        [
          'issueDetails',
          appId,
          comment.issue.id,
          ['comment_ids', $end, $set([comment.issue.id])],
          [
            'stream',
            $end,
            $set([
              {
                data_type: 'comments',
                id: comment.id,
              },
            ]),
          ],
          ['comments', comment.id, $set(comment)],
        ],
      ],
      state
    );
  },

  [ActionTypes.SUBMIT_NEW_COMMENT_FAIL](state) {
    return update(['savingStates', 'newComment', $set(false)], state);
  },

  [ActionTypes.UPDATE_COMMENT](state, { commentId }) {
    return update(['savingStates', 'commentId', $set(commentId)], state);
  },

  [ActionTypes.UPDATE_COMMENT_FAIL](
    state,
    { appId, issueId, commentId, error }
  ) {
    return update(
      [
        ['savingStates', $set(undefined)],
        [
          'issueDetails',
          appId,
          issueId,
          'comments',
          commentId,
          'errorMsg',
          $set(error.message),
        ],
      ],
      state
    );
  },

  [ActionTypes.UPDATE_COMMENT_DONE](state, { appId, issueId, comment }) {
    return update(
      [
        ['savingStates', $set(undefined)],
        ['commentIdBeingEdited', $set(undefined)],
        ['issueDetails', appId, issueId, 'comments', comment.id, $set(comment)],
      ],
      state
    );
  },

  [ActionTypes.START_COMMENT_EDIT](state, { comment }) {
    return updateSingleKey('commentIdBeingEdited', comment.id, state);
  },

  [ActionTypes.STOP_COMMENT_EDIT](state, { appId, issueId, commentId }) {
    return update(
      [
        ['commentIdBeingEdited', $set(undefined)],
        [
          'issueDetails',
          appId,
          issueId,
          'comments',
          commentId,
          'errorMsg',
          $set(undefined),
        ],
      ],
      state
    );
  },

  [ActionTypes.TOGGLE_ISSUE_TAG](state, { appId, issueId, tagId, isAdding }) {
    return toggleIssueTag(state, appId, issueId, tagId, isAdding);
  },

  [ActionTypes.TOGGLE_ISSUE_TAG_DONE](state, { appId, result, timestampId }) {
    result = _.defaults({}, result);
    result.updatedTimestampId = timestampId;
    return refreshIssue(appId, result, state);
  },

  [ActionTypes.TOGGLE_ISSUE_TAG_FAIL](
    state,
    { appId, issueId, tagId, isAdding }
  ) {
    return toggleIssueTag(state, appId, issueId, tagId, !isAdding);
  },

  [ActionTypes.TOGGLE_ISSUE_ACTION](
    state,
    { appId, issueId, category, actionKey, isAdding }
  ) {
    return toggleIssueAction(
      state,
      appId,
      issueId,
      category,
      actionKey,
      isAdding
    );
  },

  [ActionTypes.TOGGLE_ISSUE_ACTION_DONE](
    state,
    { appId, result, timestampId }
  ) {
    result = _.defaults({}, result);
    result.updatedTimestampId = timestampId;

    return refreshIssue(appId, result, state);
  },

  [ActionTypes.TOGGLE_ISSUE_ACTION_FAIL](
    state,
    { appId, issueId, category, actionKey, isAdding }
  ) {
    return toggleIssueAction(
      state,
      appId,
      issueId,
      category,
      actionKey,
      !isAdding
    );
  },

  [ActionTypes.SET_EDITING_SIDEBAR_SECTION](state, { sectionName }) {
    return updateSingleKey('sidebarSectionBeingEdited', sectionName, state);
  },

  [ActionTypes.LOAD_LOKI_TAGS_DONE](state, { result }) {
    return updateSingleKey('issueTags', result.results, state);
  },

  [ActionTypes.SET_CURRENT_APP](state, { appId }) {
    return updateSingleKey('currentAppId', appId, state);
  },

  [ActionTypes.SET_CURRENT_CLI_APP_ID](state, { appId }) {
    return updateSingleKey('currentCliAppId', appId, state);
  },

  [ActionTypes.SET_CURRENT_ISSUE_ID](state, { issueId }) {
    return updateSingleKey('currentIssueId', issueId, state);
  },

  [ActionTypes.LOAD_TAGGABLE_USERS_DONE](state, { taggableUsers }) {
    return updateSingleKey('taggableUsers', taggableUsers, state);
  },

  [ActionTypes.SET_CURRENT_CLI_APP_VERSION](state, { appVersion }) {
    return updateSingleKey('currentCliAppVersion', appVersion, state);
  },

  [ActionTypes.LOAD_APP_EVENT_STATS](state) {
    return updateLoadingState('eventStats', true, state);
  },

  [ActionTypes.LOAD_APP_EVENT_STATS_DONE](state, { eventStats }) {
    return _.flow(
      _.partial(updateSingleKey, 'eventStats', eventStats),
      _.partial(updateLoadingState, 'eventStats', false)
    )(state);
  },

  [ActionTypes.LOAD_APP_EVENT_STATS_FAIL](state) {
    return _.flow(
      _.partial(updateSingleKey, 'eventStats', undefined),
      _.partial(updateLoadingState, 'eventStats', false)
    )(state);
  },

  [ActionTypes.LOAD_APP_EVENTS](state) {
    return updateLoadingState('eventList', true, state);
  },

  [ActionTypes.LOAD_APP_EVENTS_DONE](state, { events }) {
    return _.flow(
      _.partial(updateSingleKey, 'eventList', events),
      _.partial(updateLoadingState, 'eventList', false)
    )(state);
  },

  [ActionTypes.LOAD_APP_EVENTS_FAIL](state) {
    return _.flow(
      _.partial(updateSingleKey, 'eventList', undefined),
      _.partial(updateLoadingState, 'eventList', false)
    )(state);
  },

  [ActionTypes.CLEAR_APP_EVENTS](state) {
    return updateSingleKey('eventList', undefined, state);
  },

  [ActionTypes.LOAD_APP_DEPLOY_STATUS](state) {
    return updateLoadingState('deployStatus', true, state);
  },

  [ActionTypes.LOAD_APP_DEPLOY_STATUS_DONE](state, { result }) {
    return _.flow(
      _.partial(updateSingleKey, 'deployStatus', result),
      _.partial(updateLoadingState, 'deployStatus', false)
    )(state);
  },

  [ActionTypes.LOAD_APP_DEPLOY_STATUS_FAIL](state) {
    return _.flow(
      _.partial(updateSingleKey, 'deployStatus', {}),
      _.partial(updateLoadingState, 'deployStatus', false)
    )(state);
  },

  [ActionTypes.LOAD_APP_DEPLOY_HISTORY](state) {
    return updateLoadingState('deployHistory', true, state);
  },

  [ActionTypes.LOAD_APP_DEPLOY_HISTORY_DONE](state, { result }) {
    return _.flow(
      _.partial(updateSingleKey, 'deployHistory', result),
      _.partial(updateLoadingState, 'deployHistory', false)
    )(state);
  },

  [ActionTypes.LOAD_APP_DEPLOY_HISTORY_FAIL](state) {
    return _.flow(
      _.partial(updateSingleKey, 'deployHistory', []),
      _.partial(updateLoadingState, 'deployHistory', false)
    )(state);
  },

  [ActionTypes.CLONE_APP](state) {
    return updateLoadingState('cloning', true, state);
  },

  [ActionTypes.CLONE_APP_DONE](state, { app }) {
    return _.flow(
      _.partial(updateSingleKey, 'cloneAppId', app.id),
      _.partial(updateLoadingState, 'cloning', false)
    )(state);
  },

  [ActionTypes.CLONE_APP_FAIL](state) {
    return updateLoadingState('cloning', false, state);
  },

  [ActionTypes.CLEAR_CLONE_APP_ID](state) {
    return updateSingleKey('cloneAppId', undefined, state);
  },

  [ActionTypes.LOAD_APP_USAGE_STATS](state) {
    return updateLoadingState('usageStats', true, state);
  },

  [ActionTypes.LOAD_APP_USAGE_STATS_DONE](state, { usageStats }) {
    return _.flow(
      _.partial(updateSingleKey, 'usageStats', usageStats),
      _.partial(updateLoadingState, 'usageStats', false)
    )(state);
  },

  [ActionTypes.LOAD_APP_USAGE_STATS_FAIL](state) {
    return _.flow(
      _.partial(updateSingleKey, 'usageStats', undefined),
      _.partial(updateLoadingState, 'usageStats', false)
    )(state);
  },

  [ActionTypes.DELETE_APP](state, { appId }) {
    return updateAppBeingDeleted(appId, true, state);
  },

  [ActionTypes.DELETE_APP_FAIL](state, { appId }) {
    return updateAppBeingDeleted(appId, undefined, state);
  },

  [ActionTypes.GET_APP_DELETE_STATUS_FAIL](state, { appId }) {
    return updateAppBeingDeleted(appId, undefined, state);
  },

  [ActionTypes.LOAD_DELETED_APPS](state) {
    return updateSingleKey('appsBeingDeleted', {}, state);
  },

  [ActionTypes.LOAD_DELETED_APPS_DONE](state, { deletedApps }) {
    return deletedApps.reduce((newState, appId) => {
      return update(['appsBeingDeleted', appId, $set(true)], newState);
    }, state);
  },

  [ActionTypes.LOAD_DELETED_APPS_FAIL](state) {
    return updateSingleKey('appsBeingDeleted', {}, state);
  },

  [ActionTypes.CLEAR_DEV_ERRORS](state) {
    return updateSingleKey('errors', [], state);
  },

  [ActionTypes.CLEAR_DEV_ERRORS_SCROLL](state) {
    return updateSingleKey('didScrollErrors', true, state);
  },

  [ActionTypes.CREATE_DEV_APP](state) {
    // Show "saving" state on start
    return updateSingleKey('savingApp', true, state);
  },

  [ActionTypes.CREATE_DEV_APP_DONE](state, { newApp }) {
    const appData = {
      id: newApp.id,
    };

    return _.flow(
      _.partial(updateSingleKey, 'newApp', appData),
      _.partial(updateSingleKey, 'savingApp', false)
    )(state);
  },

  [ActionTypes.CREATE_DEV_APP_FAIL](state, formErrors) {
    // Revert "saving" state on fail
    return _.flow(
      _.partial(updateFormErrors, formErrors),
      _.partial(updateSingleKey, 'savingApp', false)
    )(state);
  },

  [ActionTypes.UPDATE_CREATE_DEV_APP_FORM](state, { value, fieldName }) {
    const app = { ...state.newApp, [fieldName]: value };

    return _.flow(
      _.partial(cleanErrorsOnUpdate, fieldName, value),
      _.partial(updateSingleKey, 'newApp', app)
    )(state);
  },

  [ActionTypes.UPDATE_META_DEV_APP_FORM](state, { value, fieldName }) {
    const app = { ...state.app, [fieldName]: value };

    return _.flow(
      _.partial(cleanErrorsOnUpdate, fieldName, value),
      _.partial(updateSingleKey, 'app', app)
    )(state);
  },

  [ActionTypes.UPDATE_META_DEV_CLI_APP_FORM](state, { value, fieldName }) {
    const appData = { ...state.app, [fieldName]: value };

    return _.flow(
      _.partial(cleanErrorsOnUpdate, fieldName, value),
      _.partial(updateSingleKey, 'app', appData)
    )(state);
  },

  [ActionTypes.UPDATE_DEV_APP](state, { app }) {
    return updateSingleKey('app', app, state);
  },

  [ActionTypes.UPDATE_DEV_APP_META](state) {
    // Show "saving" state on start
    return updateSingleKey('savingApp', true, state);
  },

  [ActionTypes.UPDATE_DEV_APP_META_DONE](state) {
    return updateSingleKey('savingApp', false, state);
  },

  [ActionTypes.UPDATE_DEV_APP_META_FAIL](state, formErrors) {
    // Revert "saving" state on fail
    return _.flow(
      _.partial(updateFormErrors, formErrors),
      _.partial(updateSingleKey, 'savingApp', false)
    )(state);
  },

  [ActionTypes.UPDATE_DEV_APP_META_IMAGE_DONE](state, { result }) {
    return update(['app', 'image', $set(result.image)], state);
  },

  [ActionTypes.UPDATE_DEV_APP_META_IMAGE_FAIL](state, formErrors) {
    return _.flow(
      _.partial(updateFormErrors, formErrors),
      _.partial(updateSingleKey, 'savingApp', false)
    )(state);
  },

  [ActionTypes.GET_DEV_APP_FORM_DONE](state, { formId, fields }) {
    return update(['formFields', formId, $set(fields)], state);
  },

  [ActionTypes.GET_DEV_APP_FORM_FAIL](state, { formId }) {
    return update(['formFields', formId, $set(undefined)], state);
  },

  [ActionTypes.UPDATE_CLI_ADD_ADMIN_FORM](state, { value, fieldName }) {
    return _.flow(
      _.partial(cleanErrorsOnUpdate, fieldName, value),
      _.partial(updateSingleKey, 'collaboratorEmail', value)
    )(state);
  },

  [ActionTypes.UPDATE_CLI_ADD_INVITEE_FORM](state, { value, fieldName }) {
    return _.flow(
      _.partial(cleanErrorsOnUpdate, fieldName, value),
      _.partial(updateSingleKey, 'inviteeEmail', value)
    )(state);
  },

  [ActionTypes.UPDATE_NEW_TEAM_MEMBER](state, { values }) {
    return _.flow(
      _.partial(updateSavedState, 'teamMemberEmail', values.teamMemberEmail),
      _.partial(updateSavedState, 'teamMemberRole', values.teamMemberRole),
      _.partial(updateSavedState, 'teamMemberWelcome', values.teamMemberWelcome)
    )(state);
  },

  [ActionTypes.SUBMIT_NEW_TEAM_MEMBER](state) {
    return _.flow(
      _.partial(updateSaveErrorMessage, 'newTeamMember', undefined),
      update(['savingStates', 'newTeamMember', $set(true)])
    )(state);
  },

  [ActionTypes.SUBMIT_NEW_TEAM_MEMBER_DONE](state, { message }) {
    return createMessage({ type: 'success', message }, state);
  },

  [ActionTypes.SUBMIT_NEW_TEAM_MEMBER_FAIL](state, { error }) {
    return _.flow(
      update(['savingStates', 'newTeamMember', $set(false)]),
      _.partial(updateSaveErrorMessage, 'newTeamMember', error)
    )(state);
  },

  [ActionTypes.RESET_NEW_TEAM_MEMBER_FORM](state) {
    return _.flow(
      _.partial(updateSavedState, 'teamMemberEmail', undefined),
      _.partial(updateSavedState, 'teamMemberRole', undefined),
      _.partial(updateSavedState, 'teamMemberWelcome', undefined),
      _.partial(updateSaveErrorMessage, 'newTeamMember', undefined),
      update(['savingStates', 'newTeamMember', $set(false)])
    )(state);
  },

  [ActionTypes.LOAD_CLI_APP_USAGE](state) {
    return updateLoadingState('cliUsage', true, state);
  },

  [ActionTypes.LOAD_CLI_APP_USAGE_DONE](state, { usage }) {
    return _.flow(
      _.partial(updateSingleKey, 'cliUsage', usage),
      _.partial(updateLoadingState, 'cliUsage', false)
    )(state);
  },

  [ActionTypes.LOAD_CLI_APP_USAGE_FAIL](state) {
    return _.flow(
      _.partial(updateSingleKey, 'cliUsage', undefined),
      _.partial(updateLoadingState, 'cliUsage', false)
    )(state);
  },

  [ActionTypes.LOAD_CLI_APP_STATS](state) {
    return updateLoadingState('cliStats', true, state);
  },

  [ActionTypes.LOAD_CLI_APP_STATS_DONE](state, { stats }) {
    return _.flow(
      _.partial(updateSingleKey, 'cliStats', stats),
      _.partial(updateLoadingState, 'cliStats', false)
    )(state);
  },

  [ActionTypes.LOAD_CLI_APP_STATS_FAIL](state) {
    return _.flow(
      _.partial(updateSingleKey, 'cliStats', _.cloneDeep(EmptyAppStatsShape)),
      _.partial(updateLoadingState, 'cliStats', false)
    )(state);
  },

  [ActionTypes.UPDATE_APP_ACTIVATION_FORM](state, { value, fieldName }) {
    const activation = { ...state.activation, [fieldName]: value };

    return _.flow(
      _.partial(cleanErrorsOnUpdate, fieldName, value),
      _.partial(updateSingleKey, 'activation', activation)
    )(state);
  },

  [ActionTypes.SUBMIT_APP_ACTIVATION_FORM](state) {
    // Show "saving" state on start
    return updateSingleKey('savingApp', true, state);
  },

  [ActionTypes.SUBMIT_APP_ACTIVATION_FORM_DONE](state) {
    return updateSingleKey('savingApp', false, state);
  },

  [ActionTypes.SUBMIT_APP_ACTIVATION_FORM_FAIL](state, formErrors) {
    // Revert "saving" state on fail
    return _.flow(
      _.partial(updateFormErrors, formErrors),
      _.partial(updateSingleKey, 'savingApp', false)
    )(state);
  },

  [ActionTypes.LOAD_APP_ISSUES](state) {
    return _.flow(_.partial(updateLoadingState, 'isLoadingIssues', false))(
      state
    );
  },

  [ActionTypes.LOAD_APP_ISSUES_DONE](state, issues) {
    return _.flow(
      _.partial(updateSingleKey, 'issues', issues.result),
      _.partial(updateLoadingState, 'isLoadingIssues', true)
    )(state);
  },

  [ActionTypes.LOAD_APP_ISSUE_DONE](state, { appId, result }) {
    return update(['issueDetails', appId, result.id, $set(result)], state);
  },

  [ActionTypes.PROMOTE_CLI_APP](state) {
    return updateLoadingState('cliPromote', true, state);
  },

  [ActionTypes.PROMOTE_CLI_APP_DONE](state) {
    return updateLoadingState('cliPromote', false, state);
  },

  [ActionTypes.PROMOTE_CLI_APP_FAIL](
    state,
    { error: { errors, activationInfo } }
  ) {
    const cliPromote = { errors, activationInfo };
    return updateSingleKey('cliPromote', cliPromote, state);
  },

  [ActionTypes.CLEAR_PROMOTE_CLI_APP_ERRORS](state) {
    return updateSingleKey(
      'cliPromote',
      {
        activationUrl: undefined,
        errors: [],
      },
      state
    );
  },

  [ActionTypes.CREATE_MESSAGE](state, { messageType, message }) {
    return createMessage({ type: messageType, message }, state);
  },

  [ActionTypes.CLEAR_MESSAGE](state, { payload: { id: msgId } }) {
    return update(['messages', $each, ({ id }) => id === msgId, $none], state);
  },
};

export default createReducer(handlers, initialState);
